import { useEffect, useRef, useState } from "react";
import { ArrowRight, CalendarCheck, LoaderCircle, ShieldCheck } from "lucide-react";
import { trackCtaClick } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { FormPrivacyNotice, RequiredFieldHint } from "@/components/site/FormPrivacyNotice";

const BOOKING_URL =
  "https://outlook.office.com/book/OXORYMeetMeCahitTEmizkan@oxory.net/?ismsaljsauthenabled";
const WIDGET_TARGET = "#book-meeting";
const LOAD_TIMEOUT_MS = 20000;
const EXTERNAL_MEDIA_KEY = "oxory-external-media";

type WidgetStatus = "loading" | "loaded" | "error";
type WidgetErrorReason = "iframe_error" | "timeout";
type Locale = "en" | "tr";

const COPY = {
  en: {
    dateLocale: "en-GB",
    heading: "Book a meeting directly",
    lead: "Prefer to skip the back-and-forth? Pick a slot that works for you and we'll meet there.",
    loading: "Loading booking calendar…",
    loaded: "Booking calendar loaded.",
    errorIframe:
      "The booking calendar could not be loaded. Use the button below to schedule a meeting instead.",
    errorTimeout:
      "The booking calendar is taking too long to load. Use the button below to schedule a meeting instead.",
    errorTitleTimeout: "The booking calendar is taking too long to load.",
    errorTitleIframe: "The booking calendar could not be loaded.",
    errorBodyTimeout: "The booking service did not respond in time. Check your connection.",
    errorBodyIframe:
      "This can happen if the booking page blocks embedding or the connection is slow.",
    nextStep:
      "Next step: use the button below to open the scheduler in a new tab and pick your slot there.",
    confirmed: "Meeting confirmed",
    confirmedStatus: "Your meeting is confirmed.",
    date: "Date",
    time: "Time",
    withLabel: "With",
    inviteNote:
      "You will receive a calendar invitation by email. Reply to it if you need to reschedule.",
    manualSummary: "Already booked? Show your confirmation",
    showConfirmation: "Show confirmation",
    activateWidget: "Activate booking widget",
    consentNotice:
      "This calendar is provided by Microsoft Outlook Bookings. Loading it transmits data such as your IP address to Microsoft, potentially in the USA.",
    privacyPrefix: "More about booking in our",
    privacyLink: "privacy policy",
    privacyTo: "/privacy" as const,
    loadCalendar: "Load booking calendar",
    alwaysLoad: "Always load external calendar",
    requiredHint: "* required",
    iframeTitle: "Schedule a meeting with OXORY via Outlook Bookings",
    cta: "Schedule Online a Meeting with us",
    newTab: "(opens in a new tab)",
  },
  tr: {
    dateLocale: "tr-TR",
    heading: "Doğrudan görüşme planlayın",
    lead: "E-posta trafiğiyle uğraşmayın: size uygun bir saati seçin, orada buluşalım.",
    loading: "Randevu takvimi yükleniyor…",
    loaded: "Randevu takvimi yüklendi.",
    errorIframe:
      "Randevu takvimi yüklenemedi. Görüşme planlamak için aşağıdaki düğmeyi kullanın.",
    errorTimeout:
      "Randevu takvimi çok uzun sürdüğü için yüklenemedi. Görüşme planlamak için aşağıdaki düğmeyi kullanın.",
    errorTitleTimeout: "Randevu takvimi çok uzun süredir yükleniyor.",
    errorTitleIframe: "Randevu takvimi yüklenemedi.",
    errorBodyTimeout: "Randevu servisi zamanında yanıt vermedi. Bağlantınızı kontrol edin.",
    errorBodyIframe:
      "Bu durum, randevu sayfası gömülmeyi engellediğinde veya bağlantı yavaş olduğunda oluşabilir.",
    nextStep:
      "Sonraki adım: aşağıdaki düğmeyle takvimi yeni sekmede açın ve saatinizi orada seçin.",
    confirmed: "Görüşme onaylandı",
    confirmedStatus: "Görüşmeniz onaylandı.",
    date: "Tarih",
    time: "Saat",
    withLabel: "Kiminle",
    inviteNote:
      "Takvim davetini e-posta ile alacaksınız. Saati değiştirmeniz gerekirse davete yanıt verin.",
    manualSummary: "Zaten randevu aldınız mı? Onayınızı görüntüleyin",
    showConfirmation: "Onayı göster",
    activateWidget: "Randevu takvimini etkinleştir",
    consentNotice:
      "Bu takvim Microsoft Outlook Bookings tarafından sağlanır. Takvimi yüklediğinizde IP adresiniz gibi veriler, ABD dâhil olmak üzere Microsoft'a aktarılabilir.",
    privacyPrefix: "Randevu sürecine ilişkin ayrıntılar",
    privacyLink: "gizlilik politikamızda",
    privacyTo: "/tr/gizlilik" as const,
    loadCalendar: "Randevu takvimini yükle",
    alwaysLoad: "Harici takvimi her zaman yükle",
    requiredHint: "* zorunlu alan",
    iframeTitle: "Outlook Bookings üzerinden OXORY ile görüşme planlayın",
    cta: "Online görüşme planlayın",
    newTab: "(yeni sekmede açılır)",
  },
} as const;

function logWidgetError(reason: WidgetErrorReason) {
  // Structured client-side logging so unreachable-widget incidents can be
  // aggregated from console telemetry.
  console.error("[booking-widget] Booking widget unreachable", {
    reason,
    url: BOOKING_URL,
    target: WIDGET_TARGET,
    timeoutMs: LOAD_TIMEOUT_MS,
    timestamp: new Date().toISOString(),
  });
}

type BookingConfirmation = {
  date: string;
  time: string;
  person: string;
};

const DEFAULT_HOST = "Cahit T. Emizkan (OXORY)";

function formatBookingDate(value: string, dateLocale: string) {
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(dateLocale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function BookMeetingSection({
  announcement,
  locale = "en",
}: {
  announcement?: string;
  locale?: Locale;
}) {
  const t = COPY[locale];
  const [widgetActivated, setWidgetActivated] = useState(false);
  const [alwaysLoad, setAlwaysLoad] = useState(false);
  const [widgetStatus, setWidgetStatus] = useState<WidgetStatus>("loading");
  const [errorReason, setErrorReason] = useState<WidgetErrorReason | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>(t.loading);
  const [confirmation, setConfirmation] = useState<BookingConfirmation | null>(null);
  const [draft, setDraft] = useState<BookingConfirmation>({
    date: "",
    time: "",
    person: DEFAULT_HOST,
  });
  const loadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);
  const bookingFrameRef = useRef<HTMLIFrameElement | null>(null);

  // When the widget fails, move focus to the error message and scroll it into
  // view so the user immediately sees what happened and what to do next.
  useEffect(() => {
    if (widgetStatus !== "error") return;
    const node = errorRef.current;
    if (!node) return;
    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    node.scrollIntoView?.({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "center" });
    node.focus({ preventScroll: true });
  }, [widgetStatus]);

  function clearLoadTimer() {
    if (loadTimerRef.current !== null) {
      clearTimeout(loadTimerRef.current);
      loadTimerRef.current = null;
    }
  }

  function handleWidgetLoad() {
    clearLoadTimer();
    setWidgetStatus("loaded");
    setStatusMessage(t.loaded);
  }

  function handleWidgetError() {
    clearLoadTimer();
    setErrorReason("iframe_error");
    setWidgetStatus("error");
    setStatusMessage(t.errorIframe);
    logWidgetError("iframe_error");
  }

  function startLoadWatch() {
    clearLoadTimer();
    loadTimerRef.current = setTimeout(() => {
      setWidgetStatus((current) => {
        if (current === "loading") {
          setErrorReason("timeout");
          setStatusMessage(t.errorTimeout);
          logWidgetError("timeout");
          return "error";
        }
        return current;
      });
    }, LOAD_TIMEOUT_MS);
  }

  useEffect(() => {
    try {
      if (window.localStorage.getItem(EXTERNAL_MEDIA_KEY) === "true") {
        setAlwaysLoad(true);
        setWidgetActivated(true);
      }
    } catch {
      // Storage can be unavailable in private browsing; explicit click still works.
    }
    return clearLoadTimer;
  }, []);

  useEffect(() => {
    if (!widgetActivated) return;
    setWidgetStatus("loading");
    setStatusMessage(t.loading);
    startLoadWatch();
    return clearLoadTimer;
  }, [widgetActivated, t.loading]);

  // Outlook Bookings runs cross-origin, so a completed booking can only be
  // detected when the widget posts a message back to us.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== new URL(BOOKING_URL).origin) return;
      if (!bookingFrameRef.current?.contentWindow || event.source !== bookingFrameRef.current.contentWindow) return;
      const payload = event.data as Record<string, unknown> | null;
      if (!payload || typeof payload !== "object") return;
      const type = String(payload["type"] ?? payload["eventName"] ?? "").toLowerCase();
      if (!type.includes("book") || !type.includes("confirm")) return;
      const date = typeof payload["date"] === "string" ? payload["date"] : "";
      const time = typeof payload["time"] === "string" ? payload["time"] : "";
      const person = typeof payload["staff"] === "string" ? payload["staff"] : DEFAULT_HOST;
      if (!date && !time) return;
      setConfirmation({ date, time, person });
      setStatusMessage(t.confirmedStatus);
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [t]);

  function handleWidgetActivate(e: React.MouseEvent<HTMLButtonElement>) {
    const targetId = e.currentTarget.dataset["ctaTarget"] ?? WIDGET_TARGET;
    trackCtaClick(
      "Book a meeting widget",
      "booking_widget",
      targetId,
      `booking_widget ${targetId}`,
      e.clientY,
    );
    if (alwaysLoad) {
      try {
        window.localStorage.setItem(EXTERNAL_MEDIA_KEY, "true");
      } catch {
        // Consent remains valid for this page view when storage is unavailable.
      }
    }
    setWidgetActivated(true);
  }

  function handleConfirmSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.date || !draft.time) return;
    setConfirmation({ ...draft, person: draft.person.trim() || DEFAULT_HOST });
    setStatusMessage(t.confirmedStatus);
  }

  return (
    <section id="book-meeting" tabIndex={-1} className="section-shell pb-20" data-booking-section>
      <div className="rounded-lg border border-border bg-card p-5 shadow-card sm:p-6 lg:p-8">
        <p aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement ?? ""}
        </p>
        <h2 className="font-display text-lg font-bold sm:text-xl">{t.heading}</h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t.lead}</p>
        {confirmation && (
          <div
            role="status"
            aria-live="polite"
            data-booking-confirmation
            className="mt-5 rounded-md border border-primary/30 bg-primary/5 p-4 sm:p-5"
          >
            <p className="flex items-center gap-2 text-sm font-bold text-primary">
              <CalendarCheck className="size-4 shrink-0" aria-hidden="true" />
              {t.confirmed}
            </p>
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">{t.date}</dt>
                <dd className="font-semibold text-foreground" data-booking-confirmation-date>
                  {formatBookingDate(confirmation.date, t.dateLocale)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t.time}</dt>
                <dd className="font-semibold text-foreground" data-booking-confirmation-time>
                  {confirmation.time}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t.withLabel}</dt>
                <dd className="font-semibold text-foreground" data-booking-confirmation-person>
                  {confirmation.person}
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-sm text-muted-foreground">{t.inviteNote}</p>
          </div>
        )}
        <div className="relative mt-5 overflow-hidden rounded-md border border-border sm:mt-6">
          {!widgetActivated ? (
            <div
              className="flex h-[420px] w-full flex-col items-center justify-center gap-5 bg-secondary/50 p-6 text-center sm:h-[520px] md:h-[620px] lg:h-[700px]"
              data-booking-consent-placeholder
            >
              <ShieldCheck className="size-9 text-primary" aria-hidden="true" />
              <div className="max-w-xl">
                <p className="text-sm font-semibold text-foreground">{t.consentNotice}</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t.privacyPrefix}{" "}
                  <a href={t.privacyTo} className="font-semibold text-primary underline underline-offset-4">
                    {t.privacyLink}
                  </a>
                  .
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={alwaysLoad}
                  onChange={(event) => setAlwaysLoad(event.target.checked)}
                  className="size-4 accent-primary"
                />
                {t.alwaysLoad}
              </label>
              <Button
                type="button"
                data-cta-type="booking_widget"
                data-cta-target={WIDGET_TARGET}
                onClick={handleWidgetActivate}
              >
                {t.loadCalendar}
              </Button>
            </div>
          ) : widgetStatus === "loading" && (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-card/90"
              data-booking-loading
            >
              <LoaderCircle
                className="size-6 animate-spin text-primary motion-reduce:animate-none"
                aria-hidden="true"
              />
              <p className="text-sm text-muted-foreground">{t.loading}</p>
            </div>
          )}
          {widgetActivated && widgetStatus === "error" ? (
            <div
              ref={errorRef}
              tabIndex={-1}
              className="flex h-[420px] w-full flex-col items-center justify-center gap-3 p-6 text-center outline-none focus-visible:ring-2 focus-visible:ring-ring/30 sm:h-[520px] md:h-[620px] lg:h-[700px]"
              data-booking-error
              data-error-reason={errorReason ?? undefined}
              role="alert"
              aria-live="assertive"
            >
              <p className="text-sm font-bold text-foreground">
                {errorReason === "timeout" ? t.errorTitleTimeout : t.errorTitleIframe}
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                {errorReason === "timeout" ? t.errorBodyTimeout : t.errorBodyIframe}
              </p>
              <p className="max-w-md text-sm font-semibold text-foreground" data-booking-error-next-step>
                {t.nextStep}
              </p>
            </div>
          ) : widgetActivated ? (
            <iframe
              ref={bookingFrameRef}
              title={t.iframeTitle}
              src={BOOKING_URL}
              width="100%"
              height="700"
              loading="lazy"
              className="block h-[420px] w-full sm:h-[520px] md:h-[620px] lg:h-[700px]"
              style={{ border: 0 }}
              onLoad={handleWidgetLoad}
              onError={handleWidgetError}
            />
          ) : null}
        </div>
        <p
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          data-booking-status
        >
          {statusMessage}
        </p>
        {!confirmation && (
          <details className="mt-5 rounded-md border border-border p-4" data-booking-confirm-manual>
            <summary className="cursor-pointer text-sm font-semibold">{t.manualSummary}</summary>
            <form className="mt-4 grid gap-3 sm:grid-cols-3" onSubmit={handleConfirmSubmit}>
              <div className="sm:col-span-3"><RequiredFieldHint locale={locale} /></div>
              <label className="block text-sm font-semibold">
                {t.date} <span className="text-destructive">*</span>
                <input
                  required
                  type="date"
                  className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
                  value={draft.date}
                  onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold">
                {t.time} <span className="text-destructive">*</span>
                <input
                  required
                  type="time"
                  className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
                  value={draft.time}
                  onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold">
                {t.withLabel}
                <input
                  type="text"
                  className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
                  value={draft.person}
                  onChange={(e) => setDraft({ ...draft, person: e.target.value })}
                />
              </label>
              <div className="sm:col-span-3"><FormPrivacyNotice locale={locale} /></div>
              <Button type="submit" variant="outline" className="sm:col-span-3 sm:w-auto sm:justify-self-start">
                {t.showConfirmation}
              </Button>
            </form>
          </details>
        )}

        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 sm:mt-6 sm:w-auto sm:px-6"
          onClick={() =>
            trackCtaClick("Schedule Online a Meeting with us", "primary_button", BOOKING_URL)
          }
        >
          {t.cta}
          <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
          <span className="sr-only">{t.newTab}</span>
        </a>
      </div>
    </section>
  );
}
